import type { FundDetail, Reason } from "@/data/fund-detail-types";

/**
 * Data-driven "Reasons to Consider {TICKER}" 3-card section.
 *
 * Ported from the dane etf-website `components/fund-research.tsx`
 * (`FundResearch` + `ReasonCard`), adapted to our styling tokens.
 *
 * Canonical reference: static-new-etf-website/etf-gptz.html lines 134-194
 * Section: .reasons-section → .reasons-header + .reasons-grid → .reason-card[]
 *
 * Renders nothing when `fund.reasons` is empty (data-gated — funds without
 * reasons data in DDB produce an empty array from mapReasons() and are
 * unaffected).
 */
export function FundReasonsToConsider({ fund }: { fund: FundDetail }) {
  if (fund.reasons.length === 0) return null;
  return (
    <section className="border-b border-white/[0.13]">
      <div className="flex items-baseline gap-4 px-10 py-6 border-b border-white/[0.13] max-md:px-4 max-md:py-4">
        <h2 className="text-2xl font-bold text-[var(--etf-white)] tracking-[-0.02em]">
          Reasons to Consider {fund.ticker}
        </h2>
        <span className="text-[11px] text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] tracking-[0.16em] uppercase">
          {fund.fullName}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-10 py-6 max-md:px-4 max-md:py-4">
        {fund.reasons.map((reason, i) => (
          <ReasonCard key={i} reason={reason} index={i} />
        ))}
      </div>
    </section>
  );
}

function ReasonCard({ reason, index }: { reason: Reason; index: number }) {
  // Image-overlay layout: full-bleed background image with gradient and
  // content stack. Matches static .reason-img-block / .reason-img-content.
  if (reason.image) {
    return (
      <div className="relative overflow-hidden rounded-[10px] bg-[var(--etf-black2)] border border-[var(--etf-card-border)] flex">
        <div className="relative w-full min-h-[270px] overflow-hidden">
          {/* Full-bleed background image (object-position: center 40%) */}
          <img
            src={reason.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover z-0"
            style={{ objectPosition: "center 40%" }}
          />
          {/* Gradient overlay matching static .reason-img-overlay */}
          <div
            className="absolute inset-0 z-[1]"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.58) 45%, rgba(0,0,0,0.85) 100%)",
            }}
          />
          {/* Content stack — absolutely positioned to fill the block */}
          <div className="absolute inset-0 z-[2] flex flex-col px-[26px] py-[22px] max-md:px-5 max-md:py-5">
            <span
              className="font-[family-name:'Geist_Mono','SF_Mono',ui-monospace,monospace] text-[11px] font-normal tracking-[0.05em] mb-1.5"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <div className="flex flex-col flex-1">
              <h3 className="font-[family-name:var(--font-f37bolton)] text-[17px] font-extrabold tracking-[-0.02em] text-white leading-[1.25] mb-3.5">
                {reason.title}
              </h3>
              <p
                className="font-[family-name:var(--font-instrument)] text-[12.5px] font-normal leading-[1.6] mt-auto"
                style={{ color: "rgba(255,255,255,0.78)" }}
              >
                {reason.body}
              </p>
            </div>
            {reason.footnote && (
              <p
                className="font-[family-name:'Geist_Mono','SF_Mono',ui-monospace,monospace] text-[9px] font-normal tracking-[0.03em] leading-[1.5] mt-auto pt-4"
                style={{ color: "rgba(255,255,255,0.55)" }}
              >
                {reason.footnote}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Stat/legacy layout: used when no image is present (DB-sourced reasons).
  // Matches static .reason-card with stat numbers and caption text.
  return (
    <div className="flex flex-col relative rounded-[10px] overflow-hidden bg-[var(--etf-black2)] border border-[var(--etf-card-border)]">
      <div className="flex flex-col p-[22px_26px] flex-1">
        <div className="flex items-start justify-between">
          <span className="font-[family-name:var(--font-instrument)] text-5xl font-light text-[var(--etf-white)] opacity-10">
            {String(index + 1).padStart(2, "0")}
          </span>
          {reason.badge && (
            <span className="inline-block bg-[var(--etf-orange)]/10 border border-[var(--etf-orange)]/30 text-[var(--etf-orange)] text-[10px] tracking-[0.1em] uppercase rounded-sm px-1.5 py-0.5">
              {reason.badge}
            </span>
          )}
        </div>

        <div className="flex-1 flex flex-col justify-center">
          {reason.stat && reason.statLabel && (
            <div className="mb-4">
              <div className="text-4xl font-light text-[var(--etf-white)] tracking-tight mb-1">
                {reason.stat}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.06em] text-[var(--etf-white3)]">
                {reason.statLabel}
              </div>
            </div>
          )}
          {reason.title && (
            <h3 className="text-sm font-bold text-[var(--etf-white)] mb-4 leading-[1.3]">
              {reason.title}
            </h3>
          )}
          <p className="text-xs text-[var(--etf-white2)] leading-[1.65]">{reason.body}</p>
        </div>

        {reason.footnote && (
          <p className="text-[9px] text-[var(--etf-white)] opacity-[0.32] leading-[1.5] mt-auto pt-[14px]">
            {reason.footnote}
          </p>
        )}
      </div>
    </div>
  );
}
